import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  cities: string[]
  value: string
  onChange: (city: string) => void
  placeholder: string
  mapPinIcon: ReactNode
}

export default function BuilderCityPicker({
  cities,
  value,
  onChange,
  placeholder,
  mapPinIcon,
}: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const listId = useId()

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return cities
    return cities.filter((c) => c.toLowerCase().includes(q))
  }, [cities, query])

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (rootRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  const label = value.trim() || placeholder

  return (
    <div
      ref={rootRef}
      className={`builder-city-picker${open ? ' builder-city-picker--open' : ''}`}
    >
      <button
        type="button"
        className="builder-inflow-row"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="builder-inflow-row__prefix" aria-hidden>
          {mapPinIcon}
        </span>
        <span className="builder-inflow-row__slot builder-inflow-row__slot--open">
          <span
            className={
              value.trim()
                ? 'builder-city-picker__value'
                : 'builder-inflow-row__placeholder'
            }
          >
            {label}
          </span>
        </span>
        <span className="builder-inflow-row__trail" aria-hidden>
          →
        </span>
      </button>

      {open ? (
        <div
          id={listId}
          className="builder-city-picker__panel builder-brand-dropdown"
          role="listbox"
          aria-label={t('listing.sellerCityLabel')}
        >
          {cities.length > 10 ? (
            <div className="builder-brand-dropdown__search-wrap">
              <input
                type="search"
                className="builder-brand-dropdown__search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('listing.builder.citySearchPlaceholder')}
                autoComplete="off"
                aria-label={t('listing.builder.citySearchPlaceholder')}
              />
            </div>
          ) : null}
          <div className="builder-brand-dropdown__scroll">
            <div className="builder-brand-dropdown__grid builder-brand-dropdown__grid--models">
              {filtered.map((city) => {
                const selected = city === value
                return (
                  <button
                    key={city}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`builder-brand-dropdown__item builder-brand-dropdown__item--model builder-brand-dropdown__item--full${
                      selected ? ' builder-brand-dropdown__item--selected' : ''
                    }`}
                    onClick={() => {
                      onChange(city)
                      setOpen(false)
                    }}
                  >
                    <span className="builder-brand-dropdown__name">{city}</span>
                    {selected ? <span className="builder-brand-dropdown__check" aria-hidden /> : null}
                  </button>
                )
              })}
              {filtered.length === 0 ? (
                <p className="builder-model-dropdown__empty">{t('listing.builder.citySearchEmpty')}</p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
